/**
 * RequirementsPage — /requests
 *
 * Three-tab analytics view for captured requests:
 *   List    — filterable table (type / severity / size / area / requested_by / q / include_archived)
 *   Themes  — cluster analysis
 *   Trends  — stats + trend buckets
 *
 * Mirrors MemoryDashboard + EpisodesTab patterns: debounced q, page reset on filter change,
 * three-state (loading / empty / error) on every async surface.
 */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { isAdminEmail } from '@/auth/admin';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, requirements } from '@/helpers/ApiHelper';
import type { ListRequirementsParams, StatsParams, ThemesParams } from '@/helpers/ApiHelper';
import { DETECT_PROJECTS } from '@/types/intake';
import type {
  PaginatedRequirements,
  RequirementCluster,
  RequirementOut,
  RequirementsStatsResponse,
  RequirementsThemesResponse,
} from '@/types/requirement';
import { DraftsTab } from './DraftsTab';

type ActiveTab = 'list' | 'themes' | 'trends' | 'drafts';

const TAB_LABELS: Record<ActiveTab, string> = {
  list: 'List',
  themes: 'Themes',
  trends: 'Trends',
  drafts: 'Drafts',
};

const REQ_TYPES = ['Feature', 'Bug', 'Technical Debt', 'Research', 'Other'];
const SEVERITIES = ['P0', 'P1', 'P2', 'P3'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL'];

const VALID_TABS: ActiveTab[] = ['list', 'themes', 'trends', 'drafts'];

function isValidTab(v: string | null): v is ActiveTab {
  return VALID_TABS.includes(v as ActiveTab);
}

export function RequirementsPage(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: ActiveTab = isValidTab(tabParam) ? tabParam : 'list';
  const [tab, setTab] = useState<ActiveTab>(initialTab);
  const { user } = useAuth();
  const canEdit = isAdminEmail(user?.email);
  const navigate = useNavigate();

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newTab: ActiveTab) => {
      setTab(newTab);
      setSearchParams({ tab: newTab }, { replace: true });
    },
    [setSearchParams],
  );

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="baseline" spacing={2}>
          <Typography
            variant="h4"
            sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'primary.main' }}
          >
            requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Captured product requests.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/requests/new')}
          sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
        >
          New request
        </Button>
      </Stack>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        aria-label="Requirements section"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {(Object.keys(TAB_LABELS) as ActiveTab[]).map((key) => (
          <Tab key={key} label={TAB_LABELS[key]} value={key} />
        ))}
      </Tabs>

      {tab === 'list' && (
        <Box role="tabpanel" aria-label="Requirements list">
          <RequirementsListTab canEdit={canEdit} />
        </Box>
      )}
      {tab === 'themes' && (
        <Box role="tabpanel" aria-label="Requirements themes">
          <ThemesTab />
        </Box>
      )}
      {tab === 'trends' && (
        <Box role="tabpanel" aria-label="Requirements trends">
          <TrendsTab />
        </Box>
      )}
      {tab === 'drafts' && (
        <Box role="tabpanel" aria-label="In-progress drafts">
          <DraftsTab />
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// List tab
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

interface ListTabProps {
  canEdit: boolean;
}

function RequirementsListTab({ canEdit }: ListTabProps): ReactElement {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [type, setType] = useState('');
  const [severity, setSeverity] = useState('');
  const [size, setSize] = useState('');
  const [area, setArea] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  const [project, setProject] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);

  const [data, setData] = useState<PaginatedRequirements | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<string | null>(null);

  // Debounce q — 350ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, type, severity, size, area, requestedBy, project, includeArchived]);

  const fetchData = useCallback(async () => {
    const params: ListRequirementsParams = {
      q: debouncedQ || undefined,
      type: type || undefined,
      severity: severity || undefined,
      size: size || undefined,
      area: area || undefined,
      requested_by: requestedBy || undefined,
      project: project || undefined,
      include_archived: includeArchived || undefined,
      page,
    };
    try {
      const result = await requirements.list(params);
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load requirements');
    }
  }, [debouncedQ, type, severity, size, area, requestedBy, project, includeArchived, page]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleArchive = useCallback(
    async (id: string) => {
      setArchiving(id);
      try {
        await requirements.archive(id);
        await fetchData();
      } catch (e) {
        setError(e instanceof ApiError ? e.detail : 'Archive failed');
      } finally {
        setArchiving(null);
      }
    },
    [fetchData],
  );

  const pageCount = Math.max(1, Math.ceil((data?.count ?? 0) / PAGE_SIZE));

  return (
    <Stack spacing={2}>
      {/* Filters */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 240, flex: 1 }}
          inputProps={{ 'aria-label': 'Search requirements' }}
        />
        <Select
          size="small"
          value={type}
          onChange={(e) => setType(e.target.value)}
          displayEmpty
          sx={{ minWidth: 160 }}
          inputProps={{ 'aria-label': 'Filter by type' }}
        >
          <MenuItem value="">All types</MenuItem>
          {REQ_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          displayEmpty
          sx={{ minWidth: 140 }}
          inputProps={{ 'aria-label': 'Filter by severity' }}
        >
          <MenuItem value="">All severities</MenuItem>
          {SEVERITIES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={size}
          onChange={(e) => setSize(e.target.value)}
          displayEmpty
          sx={{ minWidth: 120 }}
          inputProps={{ 'aria-label': 'Filter by size' }}
        >
          <MenuItem value="">All sizes</MenuItem>
          {SIZES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={project}
          onChange={(e) => setProject(e.target.value)}
          displayEmpty
          sx={{ minWidth: 150 }}
          inputProps={{ 'aria-label': 'Filter by project' }}
        >
          <MenuItem value="">All projects</MenuItem>
          {DETECT_PROJECTS.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          label="Area"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          sx={{ minWidth: 140 }}
          inputProps={{ 'aria-label': 'Filter by area' }}
        />
        <TextField
          size="small"
          label="Requested by"
          value={requestedBy}
          onChange={(e) => setRequestedBy(e.target.value)}
          sx={{ minWidth: 160 }}
          inputProps={{ 'aria-label': 'Filter by requester' }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Include archived</Typography>}
        />
        {data && (
          <Chip
            label={`${data.count} total`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Three states */}
      {error && (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button size="small" color="inherit" onClick={() => void fetchData()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!error && data === null && (
        <Stack spacing={1}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Stack>
      )}

      {!error && data !== null && data.results.length === 0 && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No requests yet.{' '}
            <a href="/requests/new">Create a new request</a> to capture your first one.
          </Typography>
        </Box>
      )}

      {!error && data !== null && data.results.length > 0 && (
        <>
          <TableContainer>
            <Table size="small" aria-label="Requirements list">
              <TableHead>
                <TableRow>
                  <TableCell>Problem</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Area</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Notion</TableCell>
                  {canEdit && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.results.map((req) => (
                  <RequirementRow
                    key={req.id}
                    req={req}
                    canEdit={canEdit}
                    archiving={archiving === req.id}
                    onArchive={handleArchive}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, v) => setPage(v)}
              size="small"
            />
          </Box>
        </>
      )}
    </Stack>
  );
}

interface RequirementRowProps {
  req: RequirementOut;
  canEdit: boolean;
  archiving: boolean;
  onArchive: (id: string) => Promise<void>;
}

function RequirementRow({ req, canEdit, archiving, onArchive }: RequirementRowProps): ReactElement {
  const navigate = useNavigate();

  const handleRowActivate = useCallback(() => {
    navigate(`/requests/${req.id}`);
  }, [navigate, req.id]);

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleRowActivate();
      }
    },
    [handleRowActivate],
  );

  return (
    <TableRow
      hover
      onClick={handleRowActivate}
      onKeyDown={handleRowKeyDown}
      role="link"
      tabIndex={0}
      aria-label={`View requirement: ${req.problem}`}
      sx={{
        opacity: req.archived_at ? 0.6 : 1,
        cursor: 'pointer',
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: -2,
        },
      }}
    >
      <TableCell sx={{ maxWidth: 300 }}>
        <Typography
          variant="body2"
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
          title={req.problem}
        >
          {req.problem}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip label={req.type} size="small" variant="outlined" />
      </TableCell>
      <TableCell>{req.severity ?? '—'}</TableCell>
      <TableCell>{req.size ?? '—'}</TableCell>
      <TableCell>{req.area ?? '—'}</TableCell>
      <TableCell>
        {req.projects.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {req.projects.map((p) => (
              <Chip key={p} label={p} size="small" variant="outlined" color="primary" />
            ))}
          </Stack>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {req.requested_by}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="caption" color="text.secondary">
          {new Date(req.created_at).toLocaleDateString()}
        </Typography>
      </TableCell>
      <TableCell>
        {req.notion_task_id ? (
          <Chip label="notion" size="small" color="primary" variant="outlined" />
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      {canEdit && (
        <TableCell align="right">
          {req.archived_at ? (
            <Chip label="archived" size="small" variant="outlined" />
          ) : (
            <Tooltip title="Archive requirement">
              <span>
                <Button
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    void onArchive(req.id);
                  }}
                  disabled={archiving}
                  aria-label={`Archive requirement ${req.id}`}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  {archiving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <ArchiveOutlinedIcon fontSize="small" />
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Themes tab
// ---------------------------------------------------------------------------

function ThemesTab(): ReactElement {
  const [params] = useState<ThemesParams>({ min_cluster_size: 2 });
  const [data, setData] = useState<RequirementsThemesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await requirements.themes(params);
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load themes');
    }
  }, [params]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (error) {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button size="small" color="inherit" onClick={() => void fetchData()}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (data === null) {
    return (
      <Stack spacing={2}>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={80} />
        ))}
      </Stack>
    );
  }

  if (data.clusters.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Not enough requests to form themes yet. Capture more via{' '}
          <a href="/requests/new">a new request</a>.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {data.clusters.length} theme{data.clusters.length !== 1 ? 's' : ''} · {data.unclustered.length} unclustered
      </Typography>
      {data.clusters.map((cluster, idx) => (
        <ClusterCard key={idx} cluster={cluster} />
      ))}
    </Stack>
  );
}

interface ClusterCardProps {
  cluster: RequirementCluster;
}

function ClusterCard({ cluster }: ClusterCardProps): ReactElement {
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
          {cluster.label}
        </Typography>
        <Chip label={`${cluster.size} req${cluster.size !== 1 ? 's' : ''}`} size="small" />
        {cluster.dominant_type && (
          <Chip label={cluster.dominant_type} size="small" variant="outlined" />
        )}
        {cluster.dominant_area && (
          <Chip label={cluster.dominant_area} size="small" variant="outlined" color="secondary" />
        )}
      </Stack>
      <Stack spacing={0.5}>
        {cluster.sample_problems.slice(0, 3).map((p, i) => (
          <Typography
            key={i}
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              overflow: 'hidden',
            }}
          >
            · {p}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Trends tab
// ---------------------------------------------------------------------------

function TrendsTab(): ReactElement {
  const [params] = useState<StatsParams>({ bucket: 'week' });
  const [data, setData] = useState<RequirementsStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await requirements.stats(params);
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load trends');
    }
  }, [params]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (error) {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button size="small" color="inherit" onClick={() => void fetchData()}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (data === null) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={80} sx={{ flex: 1 }} />
          ))}
        </Stack>
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  if (data.total === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No requirements to trend yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Summary stat cards */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <StatCard label="Total" value={data.total} />
        {data.avg_rounds_to_dor !== null && (
          <StatCard label="Avg rounds to DoR" value={data.avg_rounds_to_dor.toFixed(1)} />
        )}
        {data.dedupe_rate !== null && (
          <StatCard label="Dedupe rate" value={`${Math.round(data.dedupe_rate * 100)}%`} />
        )}
      </Stack>

      {/* By-type breakdown */}
      {Object.keys(data.by_type).length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            By type
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(data.by_type).map(([t, n]) => (
              <Chip key={t} label={`${t}: ${n}`} size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}

      {/* By-severity breakdown */}
      {Object.keys(data.by_severity).length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            By severity
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(data.by_severity).map(([s, n]) => (
              <Chip key={s} label={`${s}: ${n}`} size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}

      {/* Trend buckets */}
      {data.trend.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Weekly trend
          </Typography>
          <TableContainer>
            <Table size="small" aria-label="Requirements trend">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Count</TableCell>
                  {REQ_TYPES.map((t) => (
                    <TableCell key={t} align="right">
                      {t}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.trend.map((bucket) => (
                  <TableRow key={bucket.period} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{bucket.period}</TableCell>
                    <TableCell align="right">{bucket.count}</TableCell>
                    {REQ_TYPES.map((t) => (
                      <TableCell key={t} align="right">
                        {bucket.by_type[t] ?? 0}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
}

function StatCard({ label, value }: StatCardProps): ReactElement {
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        minWidth: 140,
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}
